import React, { useEffect } from 'react';
import './tisprt.css';
import { motion } from 'framer-motion';
import { 
  FaBuilding, FaHospital, FaGraduationCap, FaIndustry, 
  FaShoppingCart, FaChartLine, FaHardHat, FaHome, 
  FaBriefcase, FaRocket, FaShieldAlt, FaCloud, 
  FaTachometerAlt, FaMobileAlt, FaDatabase, FaCode,
  FaCheckCircle, FaUsers, FaCogs, FaRegEnvelope,
  FaWhatsapp, FaLock, FaGlobe
} from 'react-icons/fa';

const modules = [
  { name: 'ERP Solution', icon: <FaBuilding /> },
  { name: 'CRM', icon: <FaUsers /> },
  { name: 'HRMS', icon: <FaBriefcase /> },
  { name: 'Payroll', icon: <FaChartLine /> },
  { name: 'Attendance', icon: <FaCheckCircle /> },
  { name: 'Leave Management', icon: <FaRegEnvelope /> },
  { name: 'Inventory', icon: <FaShoppingCart /> },
  { name: 'Asset Management', icon: <FaCogs /> },
  { name: 'Project Management', icon: <FaCode /> },
  { name: 'Hospital Management', icon: <FaHospital /> },
  { name: 'School ERP', icon: <FaGraduationCap /> },
  { name: 'Citizen Services', icon: <FaGlobe /> },
  { name: 'Property Tax', icon: <FaHome /> },
  { name: 'Water Tax', icon: <FaTachometerAlt /> },
  { name: 'License Management', icon: <FaShieldAlt /> },
  { name: 'Complaint Management', icon: <FaRegEnvelope /> },
  { name: 'Visitor Management', icon: <FaUsers /> },
  { name: 'Document Management', icon: <FaDatabase /> },
  { name: 'Workflow Automation', icon: <FaCogs /> },
  { name: 'Approval System', icon: <FaCheckCircle /> },
  { name: 'Dashboard & Analytics', icon: <FaChartLine /> },
  { name: 'Reports', icon: <FaDatabase /> },
  { name: 'Notification Engine', icon: <FaRegEnvelope /> },
  { name: 'SMS Gateway', icon: <FaMobileAlt /> },
  { name: 'WhatsApp Business API', icon: <FaWhatsapp /> },
  { name: 'Email Notifications', icon: <FaRegEnvelope /> },
  { name: 'Role Based Access', icon: <FaLock /> },
  { name: 'API Integration', icon: <FaCode /> },
  { name: 'Cloud Hosting', icon: <FaCloud /> },
];

const features = [
  'Enterprise Architecture', 'Highly Secure', 'Cloud Ready', 'Scalable',
  'Fast Performance', 'Mobile Friendly', 'Real Time Reports', 'Data Analytics',
  'Role Management', 'Multi Branch Support', 'Audit Logs', 'REST APIs',
  'Backup & Recovery', '24x7 Support'
];

const users = [
  { name: 'Government Departments', icon: <FaBuilding /> },
  { name: 'Municipal Corporations', icon: <FaBuilding /> },
  { name: 'Healthcare Organizations', icon: <FaHospital /> },
  { name: 'Educational Institutes', icon: <FaGraduationCap /> },
  { name: 'Manufacturing Industries', icon: <FaIndustry /> },
  { name: 'Retail Businesses', icon: <FaShoppingCart /> },
  { name: 'Finance Companies', icon: <FaChartLine /> },
  { name: 'Construction Companies', icon: <FaHardHat /> },
  { name: 'Real Estate', icon: <FaHome /> },
  { name: 'SMEs', icon: <FaBriefcase /> },
  { name: 'Startups', icon: <FaRocket /> },
  { name: 'Corporate Enterprises', icon: <FaBuilding /> },
];

const reasons = [
  'Enterprise Security', 'Fast Deployment', 'Easy Customization', 
  'Reliable Architecture', 'Affordable', 'Trusted Support', 
  'Modern UI', 'Cloud Ready'
];

const techStack = [
  'React.js', 'Node.js', 'Express.js', 'MySQL', 'MongoDB', 
  'Tailwind CSS', 'JWT', 'REST APIs', 'AWS', 'Docker', 'GitHub'
];

function TISPRT() {
  useEffect(() => {
    document.title = "TISPRT | Enterprise Software Platform | The Innovative Solutions";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = "description";
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = "TISPRT is an enterprise software platform developed by The Innovative Solutions providing ERP, CRM, HRMS, Citizen Services, WhatsApp Integration, SMS Gateway, Workflow Automation, and Digital Transformation Solutions.";
  }, []);

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const stagger = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800 overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden bg-gradient-to-br from-indigo-900 via-blue-900 to-slate-900 text-white">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-500/20 rounded-full blur-[120px] pointer-events-none"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div initial="hidden" animate="visible" variants={fadeIn}>
              <span className="inline-block py-1 px-3 rounded-full bg-blue-500/20 text-blue-300 font-semibold text-sm mb-6 border border-blue-400/30">
                Developed & Owned by The Innovative Solutions
              </span>
              <h1 className="text-5xl lg:text-7xl font-bold mb-6 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white via-blue-100 to-slate-300">
                TISPRT™
              </h1>
              <h2 className="text-2xl lg:text-3xl font-medium text-blue-200 mb-8">
                Enterprise Software Platform
              </h2>
              <p className="text-lg text-slate-300 mb-10 leading-relaxed max-w-3xl mx-auto">
                TISPRT is a comprehensive enterprise software platform developed by The Innovative Solutions to help businesses, government organizations, educational institutions, healthcare providers, and industries automate operations, improve productivity, digitize workflows, and enhance customer communication.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button className="px-8 py-4 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold transition-all shadow-lg hover:shadow-blue-500/30 w-full sm:w-auto">
                  Explore Features
                </button>
                <button className="px-8 py-4 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold transition-all backdrop-blur-sm w-full sm:w-auto">
                  Contact Our Team
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About TISPRT Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-5xl text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeIn}>
            <h2 className="text-3xl lg:text-4xl font-bold mb-8 text-slate-900">About TISPRT</h2>
            <p className="text-lg text-slate-600 mb-6">
              TISPRT is an enterprise-grade software platform created by <strong className="text-blue-700">The Innovative Solutions</strong>.
            </p>
            <p className="text-lg text-slate-600 mb-6">
              The platform is designed to simplify complex business operations by providing intelligent digital solutions for organizations of all sizes.
            </p>
            <p className="text-lg text-slate-600">
              Our software enables organizations to automate workflows, manage employees, communicate with customers, generate reports, improve transparency, and digitally transform daily operations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Product Modules */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900">Product Modules</h2>
            <p className="text-slate-600 mt-4">A comprehensive suite of solutions to power your organization.</p>
          </div>
          
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}
          >
            {modules.map((module, index) => (
              <motion.div 
                key={index} 
                variants={fadeIn}
                className="bg-white/70 backdrop-blur-md border border-white/40 shadow-xl rounded-xl p-6 flex flex-col items-center justify-center text-center hover:-translate-y-1 transition-transform duration-300 group"
              >
                <div className="text-3xl text-blue-600 mb-4 group-hover:scale-110 transition-transform duration-300">
                  {module.icon}
                </div>
                <h3 className="font-semibold text-slate-800 text-sm">{module.name}</h3>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features & Why Choose */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeIn}>
              <h2 className="text-3xl font-bold text-slate-900 mb-8">Product Features</h2>
              <div className="grid grid-cols-2 gap-4">
                {features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <FaCheckCircle className="text-green-500 shrink-0" />
                    <span className="text-slate-700 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeIn}>
              <h2 className="text-3xl font-bold text-slate-900 mb-8">Why Choose TISPRT</h2>
              <div className="grid grid-cols-2 gap-4">
                {reasons.map((reason, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                      <FaRocket className="text-blue-600 text-sm" />
                    </div>
                    <span className="text-slate-700 font-medium">{reason}</span>
                  </div>
                ))}
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Who Can Use */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-white">Who Can Use TISPRT</h2>
            <p className="text-slate-400 mt-4">Scalable architecture built for diverse industries.</p>
          </div>
          
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
          >
            {users.map((user, i) => (
              <motion.div key={i} variants={fadeIn} className="flex flex-col items-center p-6 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                <div className="text-3xl text-blue-400 mb-4">{user.icon}</div>
                <h3 className="text-center font-medium text-slate-200">{user.name}</h3>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Official Declaration */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-500/5 rounded-full blur-[100px] pointer-events-none"></div>
        
        <div className="container mx-auto px-6 max-w-4xl relative z-10">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl border border-blue-100"
          >
            <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-8 mx-auto">
              <FaShieldAlt className="text-3xl text-blue-600" />
            </div>
            
            <h2 className="text-3xl lg:text-4xl font-bold text-center text-slate-900 mb-8">Official Product Declaration</h2>
            
            <div className="space-y-6 text-slate-700">
              <p className="text-lg font-medium text-center text-slate-900 bg-blue-50 p-4 rounded-xl">
                TISPRT is an official enterprise software platform developed, owned, maintained, and supported by The Innovative Solutions.
              </p>
              
              <p>
                The Innovative Solutions holds complete ownership of the TISPRT software platform including its architecture, source code, branding, deployment, maintenance, and customer support services.
              </p>
              
              <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <FaMobileAlt className="text-blue-600" /> 
                  DLT Header Information
                </h3>
                <p className="mb-4">
                  The official DLT Header <strong className="text-blue-700 bg-blue-100 px-2 py-1 rounded">"TISPRT"</strong> belongs to The Innovative Solutions and is used exclusively for customer communication generated through the TISPRT Enterprise Software Platform.
                </p>
                <p className="font-semibold mb-3">The DLT SMS Header is exclusively used for:</p>
                <div className="grid sm:grid-cols-2 gap-y-2 gap-x-4">
                  {[
                    'Login OTP', 'Account Verification', 'Password Reset',
                    'Customer Notifications', 'Application Updates', 'Service Alerts',
                    'Payment Confirmation', 'Invoice Notifications', 'Workflow Alerts',
                    'Support Messages', 'Registration Confirmation', 'Transaction Notifications',
                    'System Generated Messages'
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <FaCheckCircle className="text-blue-500 text-sm shrink-0" />
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-sm text-slate-500 text-center italic mt-6">
                No third party is authorized to use the TISPRT brand without written permission from The Innovative Solutions.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-20 bg-white border-t border-slate-100">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-12">Technology Stack</h2>
          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            {techStack.map((tech, i) => (
              <span key={i} className="px-6 py-3 bg-slate-50 border border-slate-200 rounded-full font-medium text-slate-700 hover:border-blue-300 hover:text-blue-600 transition-colors shadow-sm cursor-default">
                {tech}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* About The Company */}
      <section className="py-20 bg-slate-900 text-white border-b border-slate-800">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Developed by <span className="text-blue-400">The Innovative Solutions</span></h2>
              <p className="text-slate-300 mb-8 leading-relaxed">
                The Innovative Solutions is a leading Information Technology company established in 2020 specializing in Enterprise Software Development, ERP Solutions, CRM Systems, HRMS, Mobile Applications, Government Digital Platforms, AI Solutions, Cloud Computing, and Business Automation.
              </p>
            </div>
            
            <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
              <h3 className="text-xl font-semibold mb-6 border-b border-white/10 pb-4">Company Information</h3>
              <ul className="space-y-4">
                <li className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4">
                  <span className="text-slate-400 min-w-[120px]">Company Name:</span>
                  <strong className="text-white">The Innovative Solutions</strong>
                </li>
                <li className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4">
                  <span className="text-slate-400 min-w-[120px]">Established:</span>
                  <span className="text-white">2020</span>
                </li>
                <li className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4">
                  <span className="text-slate-400 min-w-[120px]">Location:</span>
                  <span className="text-white">Nashik, Maharashtra, India</span>
                </li>
                <li className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4">
                  <span className="text-slate-400 min-w-[120px]">Website:</span>
                  <a href="https://theinnovativesolutions.in" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">https://theinnovativesolutions.in</a>
                </li>
                <li className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4">
                  <span className="text-slate-400 min-w-[120px]">Email:</span>
                  <a href="mailto:info@theinnovativesolutions.in" className="text-blue-400 hover:underline">info@theinnovativesolutions.in</a>
                </li>
                <li className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4">
                  <span className="text-slate-400 min-w-[120px]">Phone:</span>
                  <a href="tel:+919665016665" className="text-blue-400 hover:underline">+91 9665016665</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-950 text-center text-slate-400">
        <div className="container mx-auto px-6">
          <p className="mb-2">© 2026 The Innovative Solutions</p>
          <p className="mb-2 text-sm">TISPRT™ is an official enterprise software platform developed and owned by The Innovative Solutions.</p>
          <p className="text-sm">All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default TISPRT;
